import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import StateObserver, { user } from 'src/StateObserver';
import { openProfile } from '../profile/profile.sequence';
import i18n from 'src/lib/i18n/i18n';
import { cancelableStatePromise } from 'src/lib/statePromise';
import { withLoading } from 'src/lib/utils';
import { Feature } from 'src/replicant/analytics';
import * as Fate from 'src/replicant/fate';
import * as Visit from 'src/replicant/visit';
import {
  getBuruFriendsCount,
  isBuruFriend,
} from 'src/replicant/friends/friends.getters';
import { track } from 'src/lib/analytics/utils';
import { startVisitSequence } from '../visit/visit.sequence';
import { fetchFullUserProfile } from './friends.utils';
import {
  UserProfileContext,
  UserProfileAction,
} from './popups/PopupUserProfile';
import { FriendshipRequest } from 'src/replicant/friends/friends.ruleset';
import { FriendProfileOpenOrigin } from './friends.analytics.schema';

/**
 * Open the Friends List popup with tabs for My Friends and Requests.
 * Ensures user has a public profile before opening.
 */
export async function openFriendsListPopup(args: {
  originFeature?: 'menu';
  tab?: 'friends' | 'requests';
}) {
  const origin = args.originFeature === 'menu' ? 'hud' : undefined;

  // Ensure user has public profile
  if (!user().publicProfile) {
    await openProfile(origin);
  }

  await openPopupPromise('popupFriendsList', {
    tab: args.tab,
    origin,
  });
}

/**
 * Start a visit to a friend's house.
 * Validates pet status and initiates visit sequence.
 * Returns true if visit actually started, false otherwise.
 */
export async function visitFriend(friendId: string): Promise<boolean> {
  const state = user();
  const now = StateObserver.now();

  // Check if visit feature is unlocked (pet must be older)
  if (!Visit.getters.isUnlocked(state)) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('userProfile.visitError.notUnlocked.title'),
      message: i18n('userProfile.visitError.notUnlocked.message'),
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
      additionalTracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
    });
    return false;
  }

  // Check if already visiting someone
  if (Visit.getters.isVisiting(state)) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('userProfile.visitError.alreadyVisiting.title'),
      message: i18n('userProfile.visitError.alreadyVisiting.message'),
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
      additionalTracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
    });
    return false;
  }

  // Check if pet is in daycare (prioritized - getVisiblePetState returns 'normal' for daycare)
  if (Fate.getters.hasPermanentStatusEffect(state, 'daycare', now)) {
    await openPopupPromise('popupModalWhite', {
      title: i18n('userProfile.visitError.daycare.title'),
      message: i18n('userProfile.visitError.daycare.message'),
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
      additionalTracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
    });
    return false;
  }

  // Get visible pet state for status checks
  const petState = Fate.getters.getVisiblePetState(state, now);

  // Check if pet is asleep
  if (petState === 'sleeping') {
    await openPopupPromise('popupModalWhite', {
      title: i18n('userProfile.visitError.asleep.title'),
      message: i18n('userProfile.visitError.asleep.message'),
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
      additionalTracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
    });
    return false;
  }

  // Check if pet is dead or dying
  if (petState === 'dead' || petState === 'dying') {
    await openPopupPromise('popupModalWhite', {
      title: i18n('userProfile.visitError.needsCare.title'),
      message: i18n('userProfile.visitError.needsCare.message'),
      tracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
      additionalTracking: {
        feature: Feature.FRIENDS._,
        $subFeature: Feature.FRIENDS.USER_PROFILE_VISIT,
      },
    });
    return false;
  }

  // The visit sequence can complete without actually starting a visit - for example, if the user
  // cancels during friend selection or the friend becomes unavailable. We need to detect whether
  // a visit truly started so the caller knows whether to close popups or not.
  //
  // When a visit actually begins, `user.visit.visitSession` is set in state. We watch for this
  // change while the sequence runs. If the session appears before the sequence ends, the visit
  // started. If the sequence ends first without setting the session, no visit occurred.
  // TODO: Why origin undefined - should we pass an origin here for analytics?
  const visitPromise = startVisitSequence({ friendId }, undefined, {
    // Do not show a separate confirmation popup here: tapping a friend in the friends list already
    // expresses the user's intent to visit, so another confirmation would be redundant and disruptive
    skipConfirmation: true,
    // Skip revival intro since we want a direct visit from the friend list
    skipRevivalIntro: true,
    sourceFeature: 'FriendList',
  });

  // Create a promise that will resolve when user.visit.visitSession becomes non-null
  const { promise: visitStartPromise, cancel: cancelVisitWatch } =
    cancelableStatePromise(({ user }) => !!user.visit.visitSession);

  try {
    // Implement a race between the visit sequence and the visit start promise
    // - If the visitPromise ends first, that means the visit didn't happen
    // - If the visitStartPromise ends first, that means the visit started successfully
    const visitStarted = await Promise.race([
      visitStartPromise.then(() => true),
      visitPromise.then(() => false),
    ]);
    // Return whether the visit actually started, so caller can decide whether to close popup or not
    return visitStarted;
  } catch (error) {
    // startVisitSequence handles its own errors internally, but catch any unexpected errors here
    await openPopupPromise('popupError', {
      message: i18n('match2.team.errors.tryAgain'),
    });
    return false;
  } finally {
    cancelVisitWatch();
  }
}

/**
 * Open the user profile popup with pre-fetched data.
 * Shows full-screen loading while fetching player data.
 */
export async function openUserProfilePopup(
  playerId: string,
  origin: FriendProfileOpenOrigin,
  profileFallback?: { name: string; photo: string },
): Promise<UserProfileAction | undefined> {
  const data = await withLoading(() => fetchFullUserProfile(playerId).get(), {
    feature: Feature.FRIENDS._,
    subFeature: Feature.FRIENDS.USER_PROFILE,
  });

  if (!data) {
    await openPopupPromise('popupError', {
      message: i18n('match2.team.errors.tryAgain'),
    });
    return undefined;
  }

  // Replace name and photo with fallback if player does not have a public profile
  const displayData =
    !data.hasPublicProfile && profileFallback
      ? { ...data, name: profileFallback.name, photo: profileFallback.photo }
      : data;

  const state = user();
  let context: UserProfileContext;
  if (isBuruFriend(state, playerId)) {
    context = 'friend';
  } else {
    const request = state.friendshipRequests[playerId] as
      | FriendshipRequest
      | undefined;
    if (request?.status === 'pending') {
      context =
        request.owner.id === state.id ? 'sent_request' : 'received_request';
    } else {
      context = 'add_friend';
    }
  }

  track('FriendProfileOpen', {
    friendId: playerId,
    friendCount: getBuruFriendsCount(state),
    isFriend: context === 'friend',
    origin,
  });

  return openPopupPromise('popupUserProfile', {
    playerId,
    context,
    data: displayData,
  });
}

/**
 * Unfriend a friend after confirming with the user.
 * Fetches the friend's profile for the confirmation popup. The profile getter
 * does not require the friend to have a pet, so unfriending never depends on it.
 * Returns true if unfriending was successful, false if user canceled or if there was an error.
 */
export async function removeFriend(friendId: string) {
  const details = await withLoading(
    () => fetchFullUserProfile(friendId).get(),
    {
      feature: Feature.FRIENDS._,
      subFeature: Feature.FRIENDS.USER_PROFILE,
    },
  );

  // Profile is null only when the player does not exist
  if (!details) {
    await openPopupPromise('popupError', {
      message: i18n(`match2.team.errors.tryAgain`),
    });
    return false;
  }

  // Show unfriend confirmation popup
  const success = await openPopupPromise('popupFriendRemoveConfirm', {
    details,
  });

  track('FriendRemoveAction', {
    action: success ? 'removed' : 'kept',
    friendID: friendId,
  });

  // Trigger unfriend action if user confirmed
  if (success) {
    await StateObserver.invoke.unfriend(friendId);

    track('FriendRemove', {
      friendID: friendId,
      friendCount: getBuruFriendsCount(user()),
    });
  }

  return success;
}
